/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DataLicensingRedistributionWhy.module.scss';

type Translate = (key: string) => string;

const getWhyItems = (t: Translate) => [
  {
    num: '01',
    title: t('Every record is read from a government registry.'),
    desc: t('400+ national registries, gazettes and filing authorities, connected at source.'),
  },
  {
    num: '02',
    title: t('Every field names the registry it came from.'),
    desc: t('Attribution sits on the field itself, rather than once on the file.'),
  },
  {
    num: '03',
    title: t('Every record links back to the filing.'),
    desc: t('A deep link to the source record, which you can pass through to your own users.'),
  },
  {
    num: '04',
    title: t('Every field carries two dates.'),
    desc: t('The date the registry recorded it, and the date we retrieved it.'),
  },
  {
    num: '05',
    title: t('The filed document comes with it.'),
    desc: t('The official filing itself, wherever the registry publishes one.'),
  },
  {
    num: '06',
    title: t('Your customers keep what they export.'),
    desc: t('Download and retention rights, under your commercial terms and your brand.'),
  },
];

export const DataLicensingRedistributionWhy = () => {
  const { t } = useInt();
  const whyItems = getWhyItems(t);
  const headRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const revealables = [headRef.current, ...rowRefs.current].filter((el): el is HTMLDivElement => Boolean(el));

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section className={styles.root}>
      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock
          title={t('What travels with every record')}
          description={t('Six properties that survive the handover into your product and reach the person using it.')}
          align="left"
        />
      </div>

      <div className={styles.list}>
        {whyItems.map((item, i) => (
          <div
            key={item.num}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            className={cn(styles.row, styles.reveal)}
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <span className={styles.num}>{item.num}</span>
            <span className={styles.body}>
              <span className={styles.title}>{item.title}</span>
              <span className={styles.desc}>{item.desc}</span>
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
};
